/**
 * Fachliches Datum: Kalendertag in Europe/Berlin als ISO-Datum `YYYY-MM-DD`.
 * Zeitpunkte bleiben `timestamptz` UTC; fachliche Daten (z. B. „Gültig ab“) sind Kalendertage in Berlin.
 * `now` kommt vom fachlichen Jetzt (`platform.clock`, Story 1.17: echte Zeit oder Testuhr).
 */

const berlinDate = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Berlin',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/** Kalendertag in Europe/Berlin zum Zeitpunkt `now`, z. B. `2026-10-02`. */
export function businessDate(now: Date): string {
  const parts = berlinDate.formatToParts(now);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

/** Zeitpunkt-Text einer Gegenstelle (z. B. Shopware `createdAt`, eBay `creationDate`) als Datum; unlesbar → `null`. */
export function parseInstant(value: unknown): Date | null {
  if (typeof value !== 'string') return null;
  const at = new Date(value);
  return Number.isNaN(at.getTime()) ? null : at;
}

/**
 * Liegt die Anlagezeit `created` vor dem Übernahmestart `start`? Kein Start → nein (Story 6.14: gleiche Regel für
 * Shopware und eBay; die Anlagezeit vorher mit `parseInstant` lesen – unlesbar heißt nein, ohne die Einstellung zu lesen).
 */
export function isCreatedBefore(created: Date, start: Date | null): boolean {
  return start !== null && created.getTime() < start.getTime();
}

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Ist `value` ein gültiges ISO-Datum `YYYY-MM-DD` (kein 30.02.)? */
export function isIsoDate(value: string): boolean {
  const m = ISO_DATE.exec(value);
  if (!m) return false;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  const d = new Date(Date.UTC(year, month - 1, day));
  return d.getUTCFullYear() === year && d.getUTCMonth() === month - 1 && d.getUTCDate() === day;
}

/** Vortag eines ISO-Datums, z. B. `2027-02-01` → `2027-01-31`. */
export function previousDay(date: string): string {
  if (!isIsoDate(date)) throw new RangeError(`Kein gültiges Datum: ${date}`);
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d - 1)).toISOString().slice(0, 10);
}

const ISO_TIME = /^([01]\d|2[0-3]):([0-5]\d)$/;

/** Ist `value` eine gültige Uhrzeit `HH:MM` (00:00–23:59)? */
export function isClockTime(value: string): boolean {
  return ISO_TIME.test(value);
}

const OFFSET_DATE_TIME = /^(\d{4}-\d{2}-\d{2})T([01]\d|2[0-3]):([0-5]\d)(?::([0-5]\d)(\.\d{1,9})?)?(Z|([+-])(\d{2}):(\d{2}))$/;

/**
 * Zeitpunkt mit Zeitzone streng lesen, z. B. `2027-02-03T02:00:00+01:00` oder `…Z`: gültiges Kalenderdatum (kein 30.02.),
 * Uhrzeit 00:00–23:59(:59), Versatz höchstens ±14:00. Sonst `null` – nie still auf einen anderen Tag verschoben.
 */
export function parseOffsetDateTime(value: string): Date | null {
  const m = OFFSET_DATE_TIME.exec(value);
  if (!m || !isIsoDate(m[1]!)) return null;
  if (m[6] !== 'Z') {
    const hours = Number(m[8]);
    const minutes = Number(m[9]);
    if (minutes > 59 || hours * 60 + minutes > 14 * 60) return null;
  }
  const at = new Date(value);
  return Number.isNaN(at.getTime()) ? null : at;
}

const LOCAL_DATE_TIME = /^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2})$/;

/**
 * Zeitpunkt aus einer Einstellung lesen: `JJJJ-MM-TT HH:MM` (deutsche Zeit, Europe/Berlin) oder ISO mit Zeitzone (wie
 * `parseOffsetDateTime`); sonst `null`. Einzige Lesart z. B. für „Bestellungen übernehmen ab“ (Shopware und eBay).
 */
export function parseBerlinOrOffsetDateTime(value: string): Date | null {
  const v = value.trim();
  const local = LOCAL_DATE_TIME.exec(v);
  if (local) {
    if (!isIsoDate(local[1]!) || !isClockTime(local[2]!)) return null;
    return berlinLocalToUtc(local[1]!, local[2]!);
  }
  return parseOffsetDateTime(v);
}

/** Folgetag eines ISO-Datums, z. B. `2027-02-28` → `2027-03-01`. */
export function nextDay(date: string): string {
  if (!isIsoDate(date)) throw new RangeError(`Kein gültiges Datum: ${date}`);
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d + 1)).toISOString().slice(0, 10);
}

const berlinWallClock = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Berlin',
  hourCycle: 'h23',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});

/** Abstand Berliner Ortszeit − UTC in Millisekunden zum Zeitpunkt `t` (+1 h Winter, +2 h Sommer). */
function berlinOffsetMs(t: number): number {
  const parts = berlinWallClock.formatToParts(new Date(t));
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? '0');
  const wall = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'));
  return wall - Math.floor(t / 1000) * 1000;
}

/**
 * Zeitpunkt (UTC) des Tagesbeginns 00:00 in Europe/Berlin am Kalendertag `date`.
 * Die Zeitumstellung liegt in Deutschland nie um Mitternacht, daher ist 00:00 immer eindeutig.
 */
export function berlinMidnight(date: string): Date {
  if (!isIsoDate(date)) throw new RangeError(`Kein gültiges Datum: ${date}`);
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const wall = Date.UTC(y, m - 1, d);
  let t = wall - berlinOffsetMs(wall);
  t = wall - berlinOffsetMs(t);
  return new Date(t);
}

/**
 * Ortszeit Europe/Berlin (Datum `YYYY-MM-DD`, Uhrzeit `HH:MM`) als Zeitpunkt (UTC), Sommerzeit beachtet.
 * Gibt es die Uhrzeit wegen der Zeitumstellung nicht (z. B. 02:30 am letzten Märzsonntag), gilt die Zeit
 * eine Stunde später; doppelte Uhrzeiten (letzter Oktobersonntag) gelten in der Sommerzeit (erstes Vorkommen).
 */
export function berlinLocalToUtc(date: string, time: string): Date {
  if (!isIsoDate(date)) throw new RangeError(`Kein gültiges Datum: ${date}`);
  const t = ISO_TIME.exec(time);
  if (!t) throw new RangeError(`Keine gültige Uhrzeit (erwartet HH:MM): ${time}`);
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const local = Date.UTC(y, m - 1, d, Number(t[1]), Number(t[2]));
  // Kandidaten mit beiden möglichen Abständen; der frühere passende gewinnt (Sommerzeit bei doppelter Stunde).
  const candidates = [local - berlinOffsetMs(local - 2 * 3_600_000), local - berlinOffsetMs(local)]
    .filter((utc, i, all) => all.indexOf(utc) === i)
    .sort((a, b) => a - b);
  for (const utc of candidates) {
    if (utc + berlinOffsetMs(utc) === local) return new Date(utc);
  }
  // Lücke der Zeitumstellung: mit dem Abstand vor der Umstellung rechnen (ergibt eine Stunde später).
  return new Date(local - berlinOffsetMs(local - 2 * 3_600_000));
}

/**
 * Zeitraum „letzte `days` Tage“ zum fachlichen Datum `asOf` (AD-35): Kalendertage Europe/Berlin, heute eingeschlossen –
 * also `asOf` und die `days − 1` Tage davor. `from` und `to` sind beide eingeschlossen. Einzige Stelle, an der Zeiträume
 * gerechnet werden (kein Modul rechnet sie selbst). Rein: kein Zugriff auf Uhr oder Datenbank.
 * Beispiel: `periodWindow('2027-03-10', 30)` → `{ from: '2027-02-09', to: '2027-03-10' }`.
 */
export function periodWindow(asOf: string, days: number): { from: string; to: string } {
  if (!isIsoDate(asOf)) throw new RangeError(`Kein gültiges Datum: ${asOf}`);
  if (!Number.isInteger(days) || days < 1) throw new RangeError(`Zeitraum muss mindestens 1 Tag sein: ${days}`);
  const [y, m, d] = asOf.split('-').map(Number) as [number, number, number];
  return { from: new Date(Date.UTC(y, m - 1, d - (days - 1))).toISOString().slice(0, 10), to: asOf };
}

/**
 * Vorjahreszeitraum (AD-35): derselbe Zeitraum ein Jahr früher – beide Grenzen je ein Kalenderjahr zurück; ein 29.02.
 * wird zum 28.02. (das Vorjahr hat keinen 29.02.). Rein. Beispiel: `{ from: '2028-01-31', to: '2028-02-29' }` →
 * `{ from: '2027-01-31', to: '2027-02-28' }`.
 */
export function previousYearWindow(window: { from: string; to: string }): { from: string; to: string } {
  const back = (date: string): string => {
    if (!isIsoDate(date)) throw new RangeError(`Kein gültiges Datum: ${date}`);
    const [y, m, d] = date.split('-').map(Number) as [number, number, number];
    const last = new Date(Date.UTC(y - 1, m, 0)).getUTCDate();
    return `${String(y - 1).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(Math.min(d, last)).padStart(2, '0')}`;
  };
  if (window.from > window.to) throw new RangeError(`Zeitraum verkehrt: ${window.from} > ${window.to}`);
  return { from: back(window.from), to: back(window.to) };
}
